import {useCallback, useEffect, useState} from 'react';

import useFilters from '@/hooks/useFilters';
import {referralService} from '@/services/referralService';
import type {
  AfriexQueryParams,
  AfriexReferral,
  ReferralListProps,
} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

const useReferralList = (): ReferralListProps => {
  const [referralList, setReferralList] = useState<AfriexReferral[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [itemTotal, setItemTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  const fetchAllReferrals = useCallback(
    async (params?: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const {data: referrals, total} = await referralService.getReferralList({
          ...params,
          ...currentFilters,
        });
        setReferralList(referrals);
        setItemTotal(total || 0);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilters],
  );

  useEffect(() => {
    // Fire-and-forget: fetchAllReferrals try/catches, stores the failure in
    // `error` and clears `isLoading`, so this only stops an unexpected throw
    // becoming an unhandled rejection.
    fetchAllReferrals({}).catch(() => {});
  }, [fetchAllReferrals]);

  const handleApplyFilters = useCallback(() => {
    const applied = applyFilters(filtersToApply);
    // Pagination reset is success-only work. applyFilters returns false when it
    // rejects a date range, and Pagination reacts to this flag by calling
    // onPageChange -> a refetch, so toggling it unconditionally fired a request
    // the user never asked for. See hooks/useFilters.ts.
    if (!applied) {
      return false;
    }

    setIsPageReset((prev) => !prev);
    return true;
  }, [applyFilters, filtersToApply]);

  // Handle change for instant data fetching
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const handleAutoComplete = useCallback(
    async (searchTerm: string) => {
      try {
        setError(null);
        setIsLoading(true);
        setShowPagination(false);
        if (!searchTerm?.trim() || searchTerm.length < 3) {
          await fetchAllReferrals({});
          return;
        }
        const referrals = await referralService.searchAutoComplete(searchTerm);
        if (referrals) {
          setReferralList(referrals);
          setItemTotal(1);
          setIsSearch(true);
        }
      } catch (e) {
        setError(e);
      }
      setIsLoading(false);
    },
    [fetchAllReferrals],
  );

  const handlePayment = useCallback(async (referralId: string) => {
    try {
      setError(null);
      setIsLoading(true);
      const referral = await referralService.fulfillReferral(referralId);
      if (referral) {
        showSuccessMessage('Referral fulfilled successfully');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (e) {
      showErrorMessage(`Payment failed:${getErrorMessage(e, String(e))}`);
    }
    setIsLoading(false);
  }, []);

  const handleBlockPayout = useCallback(async (referralId: string) => {
    try {
      setError(null);
      setIsLoading(true);
      const referral = await referralService.blockPayout(referralId);
      if (referral) {
        showSuccessMessage('Referral payout blocked successfully');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (e) {
      showErrorMessage(
        `Blocking payout failed:${getErrorMessage(e, String(e))}`,
      );
    }
    setIsLoading(false);
  }, []);

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  };

  // `ReferralListProps` declares these four as `=> void`, and the values reach
  // props declared the same way (SearchBar's `handleAutoComplete`,
  // ReferralTable/ReferralRowType's pay and block handlers,
  // `PaginationProps['onPageChange']`). Hand back synchronous wrappers rather
  // than widening those declarations. Every async body above already
  // try/catches and reports through setError or showErrorMessage, so each
  // catch here only stops an unexpected throw becoming an unhandled rejection.
  // useCallback keeps the identities stable so memoised children below do not
  // re-render on every parent render.
  const onFetchReferralList = useCallback(
    (params?: AfriexQueryParams) => {
      fetchAllReferrals(params).catch(() => {});
    },
    [fetchAllReferrals],
  );

  const onAutoComplete = useCallback(
    (searchTerm: string) => {
      handleAutoComplete(searchTerm).catch(() => {});
    },
    [handleAutoComplete],
  );

  const onPayment = useCallback(
    (referralId: string) => {
      handlePayment(referralId).catch(() => {});
    },
    [handlePayment],
  );

  const onBlockPayout = useCallback(
    (referralId: string) => {
      handleBlockPayout(referralId).catch(() => {});
    },
    [handleBlockPayout],
  );

  return {
    error,
    handlePayment: onPayment,
    showPagination,
    handleBlockPayout: onBlockPayout,
    setPage,
    isLoading,
    isSearch,
    isPageReset,
    fetchReferralList: onFetchReferralList,
    pageInfo: {
      page,
      total: itemTotal,
    },
    referralList,
    filterCount,
    currentFilters,
    filtersToApply,
    handleApplyFilters,
    clearFilters: cleanFilters,
    handleAutoComplete: onAutoComplete,
    selectAppliedFilters,
    selectCurrentFilters,
  };
};

export default useReferralList;
